// Fetch all clients for messaging (paginated endpoint returns max 20 per page)
export async function fetchClients() {
  const token = getJwtToken();
  const headers = { Authorization: token };
  let allClients = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const res = await axios.get(API_BASE + "/clients", { headers, params: { page } });
    const data = Array.isArray(res.data?.data) ? res.data.data : [];
    allClients = [...allClients, ...data];
    // If we got fewer than 20, we've reached the last page
    hasMore = data.length >= 20;
    page++;
  }

  return allClients;
}
// Create a new message thread (individual or group)
export async function createThread({ type = "individual", coachId, clientId, groupBookingId, clientIds, name }) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const payload = { type, coachId, clientId, groupBookingId, clientIds, name };
  const res = await axios.post(API_BASE + "/messages/threads", payload, { headers });
  return res.data;
}
import axios from "axios";
import { getJwtToken } from "../../storage/user";
import { GetMessageThreadsURL, GetMessageThreadDetailsURL, SendMessageURL } from "./apiURLs";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

export async function fetchMessageThreads() {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const res = await axios.get(API_BASE + GetMessageThreadsURL, { headers });
  return res.data;
}

// Get the count of unread message threads
export async function fetchUnreadMessagesCount() {
  const token = getJwtToken();
  if (!token) return 0;
  const headers = { Authorization: token };
  try {
    const res = await axios.get(API_BASE + GetMessageThreadsURL, { headers });
    const threads = res.data || [];
    return threads.filter((thread) => thread.unread).length;
  } catch {
    return 0;
  }
}

export async function fetchThreadDetails(threadId, page = 1, limit = 50) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const baseUrl = API_BASE + GetMessageThreadDetailsURL.replace(":id", threadId);
  const url = `${baseUrl}?page=${page}&limit=${limit}`;
  const res = await axios.get(url, { headers });
  return res.data;
}

export async function sendMessage(threadId, message) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const url = API_BASE + SendMessageURL.replace(":id", threadId);
  const res = await axios.post(url, message, { headers });
  return res.data;
}

// Add mimeType to attachment upload
export async function uploadAttachment(messageId, threadId, file, type) {
  const token = getJwtToken();
  const headers = token
    ? { Authorization: token, "Content-Type": "multipart/form-data" }
    : { "Content-Type": "multipart/form-data" };
  const formData = new FormData();
  formData.append("file", file);
  // Only allow 'image', 'video', or 'file' for type
  const fileType = file.type ? file.type.split("/")[0] : "";
  const safeType = ["image", "video"].includes(fileType) ? fileType : "file";
  formData.append("type", type || safeType);
  formData.append("messageId", messageId);
  if (file.type) {
    formData.append("mimeType", file.type);
  }
  if (!messageId || !file || !threadId) throw new Error("Missing messageId, threadId, or file");
  const url = `${API_BASE}/messages/threads/${threadId}/attachments`;
  try {
    const res = await axios.post(url, formData, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Attachment upload failed");
  }
}

export async function updateMessage(messageId, newText) {
  const token = getJwtToken();
  const headers = token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/${messageId}`;
  try {
    const res = await axios.put(url, { text: newText }, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Message update failed");
  }
}

// Get presigned download URL for an attachment
export async function getAttachmentDownloadUrl(attachmentId) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const url = `${API_BASE}/messages/attachments/${attachmentId}/download`;
  const res = await axios.get(url, { headers });
  return res.data.url;
}

export async function deleteMessage(messageId) {
  const token = getJwtToken();
  const headers = token ? { Authorization: token } : {};
  const url = `${API_BASE}/messages/${messageId}`;
  try {
    await axios.delete(url, { headers });
    return true;
  } catch (err) {
    throw new Error("Message delete failed");
  }
}

export async function updateThread(threadId, payload) {
  const token = getJwtToken();
  const headers = token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}`;
  try {
    const res = await axios.put(url, payload, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Thread update failed");
  }
}

export async function addThreadParticipants(threadId, clientIds) {
  const token = getJwtToken();
  const headers = token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/participants`;
  try {
    const res = await axios.post(url, { clientIds }, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to add participants");
  }
}

export async function removeThreadParticipants(threadId, clientIds) {
  const token = getJwtToken();
  const headers = token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/participants`;
  try {
    const res = await axios.delete(url, { headers, data: { clientIds } });
    return res.data;
  } catch (err) {
    throw new Error("Failed to remove participants");
  }
}

export async function markThreadUnread(threadId) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/mark-unread`;
  const res = await axios.post(url, {}, { headers });
  if (!res || !res.data) throw new Error("Failed to mark thread as unread");
  return res.data;
}

// Toggle reaction on a message (add or remove)
export async function toggleReaction(messageId, reactionType) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/${messageId}/reactions`;
  try {
    const res = await axios.post(url, { reactionType }, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to toggle reaction");
  }
}

// Mute a thread for the current user
export async function muteThread(threadId) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/mute`;
  try {
    const res = await axios.post(url, {}, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to mute thread");
  }
}

// Archive a thread
export async function archiveThread(threadId) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/archive`;
  try {
    const res = await axios.post(url, {}, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to archive thread");
  }
}

// Unarchive a thread (restore to active)
export async function unarchiveThread(threadId) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/unarchive`;
  try {
    const res = await axios.post(url, {}, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to unarchive thread");
  }
}

// Fetch archived threads
export async function fetchArchivedThreads() {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads-archived`;
  try {
    const res = await axios.get(url, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to fetch archived threads");
  }
}

// Delete a thread
export async function deleteThread(threadId) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/delete`;
  try {
    const res = await axios.delete(url, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to delete thread");
  }
}

// ========== Scheduled Messages API ==========

// Schedule a message for later delivery
export async function scheduleMessage(threadId, { text, scheduledAt, isUrgent, attachments, senderTimezone }) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/threads/${threadId}/schedule`;
  try {
    const res = await axios.post(
      url,
      { text, scheduledAt, isUrgent, attachments, timezone: senderTimezone },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || "Failed to schedule message");
  }
}

// List all pending scheduled messages for the current user
export async function fetchScheduledMessages() {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const url = `${API_BASE}/messages/scheduled`;
  try {
    const res = await axios.get(url, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to fetch scheduled messages");
  }
}

// Get a single scheduled message
export async function fetchScheduledMessage(scheduledMessageId) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const url = `${API_BASE}/messages/scheduled/${scheduledMessageId}`;
  try {
    const res = await axios.get(url, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to fetch scheduled message");
  }
}

// Update a scheduled message
export async function updateScheduledMessage(scheduledMessageId, { text, scheduledAt, senderTimezone }) {
  const token = getJwtToken();
  const headers = { Authorization: token, "Content-Type": "application/json" };
  const url = `${API_BASE}/messages/scheduled/${scheduledMessageId}`;
  try {
    const res = await axios.put(url, { text, scheduledAt, timezone: senderTimezone }, { headers });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || "Failed to update scheduled message");
  }
}

// Cancel a scheduled message
export async function cancelScheduledMessage(scheduledMessageId) {
  const token = getJwtToken();
  const headers = { Authorization: token };
  const url = `${API_BASE}/messages/scheduled/${scheduledMessageId}`;
  try {
    const res = await axios.delete(url, { headers });
    return res.data;
  } catch (err) {
    throw new Error("Failed to cancel scheduled message");
  }
}
